import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Print as PrintIcon, Timeline as TimelineIcon,
} from '@mui/icons-material';
import type { PeriodontalChart } from '../../types/odontology';
import { deletePeriodontalChart, getPeriodontalChartPrintData, listPeriodontalCharts } from '../../services/odontologyService';
import { buildPeriodontalPrintHtml } from '../../utils/periodontalPrintDocument';
import { printClinicHtml } from '../../utils/clinicPrintHtml';
import PeriodontalChartDialog, { type PeriodontalDialogMode } from './PeriodontalChartDialog';
import { formatDateBr } from './odontologyHooks';

export interface PatientPeriodontalTabProps {
  patientId: string;
  unitId?: string | null;
  linkedProfessionalId?: string | null;
  /** Dentro da aba Odontograma (sub-aba periograma): sem cabeçalho duplicado. */
  embedded?: boolean;
}

const EXAM_TYPE_LABELS: Record<string, string> = {
  initial: 'Exame inicial',
  reevaluation: 'Reavaliação',
  maintenance: 'Manutenção',
};

const PatientPeriodontalTab: React.FC<PatientPeriodontalTabProps> = ({
  patientId,
  unitId,
  linkedProfessionalId,
  embedded = false,
}) => {
  const [charts, setCharts] = useState<PeriodontalChart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<PeriodontalDialogMode>('create');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPeriodontalCharts({ patient_id: patientId, unit_id: unitId || undefined, limit: 100 });
      setCharts(res?.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar periodontogramas');
    } finally {
      setLoading(false);
    }
  }, [patientId, unitId]);

  useEffect(() => { load(); }, [load]);

  const open = (mode: PeriodontalDialogMode, id: string | null) => {
    setDialogMode(mode);
    setSelectedId(id);
    setDialogOpen(true);
  };

  const handlePrint = async (id: string) => {
    try {
      const printData = await getPeriodontalChartPrintData(id);
      const html = buildPeriodontalPrintHtml(printData);
      printClinicHtml(html, { title: 'Periodontograma' });
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao imprimir periodontograma');
    }
  };

  const handleDelete = async (chart: PeriodontalChart) => {
    if (!window.confirm(`Remover o periodontograma de ${formatDateBr(chart.exam_date)}?`)) return;
    try {
      await deletePeriodontalChart(chart.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao remover periodontograma');
    }
  };

  return (
    <Box sx={{ p: embedded ? { xs: 1.5, sm: 2 } : { xs: 1.5, sm: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: embedded ? 'flex-end' : 'space-between',
          alignItems: 'center',
          mb: embedded ? 1.5 : 2,
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {!embedded && (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon sx={{ color: '#0D9488', fontSize: 20 }} /> Periodontograma
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B' }}>
              Acompanhe a evolução dos índices entre exames
            </Typography>
          </Box>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => open('create', null)}
          sx={{ textTransform: 'none', bgcolor: '#0D9488', '&:hover': { bgcolor: '#0F766E' } }}
        >
          Novo exame
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : !charts.length ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Nenhum exame periodontal registrado. O exame inicial estabelece a linha de base para comparação.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {charts.map((chart) => (
            <Card
              key={chart.id}
              onClick={() => open('view', chart.id)}
              sx={{
                borderRadius: 2,
                border: '1px solid #E2E8F0',
                boxShadow: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#0D9488', bgcolor: '#F0FDFA' },
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F766E' }}>
                      {EXAM_TYPE_LABELS[chart.exam_type] || chart.exam_type} · {formatDateBr(chart.exam_date)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                      {chart.professional_name ? `${chart.professional_name} · ` : ''}
                      Placa {chart.summary?.plaque_index ?? 0}% · Sangramento {chart.summary?.bleeding_index ?? 0}% ·
                      {' '}PS média {chart.summary?.mean_probing_depth ?? 0} mm
                    </Typography>
                    {Number(chart.summary?.sites_pd_6_plus) > 0 && (
                      <Typography variant="caption" sx={{ color: '#DC2626', display: 'block', fontWeight: 600 }}>
                        {chart.summary?.sites_pd_6_plus} sítio(s) com bolsa ≥ 6 mm
                      </Typography>
                    )}
                    {chart.diagnosis && (
                      <Typography variant="caption" sx={{ color: '#475569', display: 'block', mt: 0.5 }}>
                        {chart.diagnosis}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <Chip
                      size="small"
                      label={chart.status === 'finalized' ? 'Finalizado' : 'Rascunho'}
                      sx={{
                        fontSize: '0.65rem', height: 20, fontWeight: 600,
                        bgcolor: chart.status === 'finalized' ? '#DCFCE7' : '#FEF3C7',
                        color: chart.status === 'finalized' ? '#16A34A' : '#D97706',
                      }}
                    />
                    <Tooltip title="Imprimir">
                      <IconButton size="small" onClick={() => handlePrint(chart.id)} sx={{ color: '#64748B' }} aria-label="Imprimir periodontograma">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => open('edit', chart.id)} sx={{ color: '#0D9488' }} aria-label="Editar periodontograma">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remover">
                      <IconButton size="small" onClick={() => handleDelete(chart)} sx={{ color: '#94A3B8' }} aria-label="Remover periodontograma">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <PeriodontalChartDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        patientId={patientId}
        unitId={unitId}
        chartId={selectedId}
        mode={dialogMode}
        defaultProfessionalId={linkedProfessionalId}
        onSaved={load}
      />
    </Box>
  );
};

export default PatientPeriodontalTab;
